import { Component, DestroyRef, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { CompanyChoice, CompanySearchResult } from 'src/app/models/company.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-company-search-dialog',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatProgressSpinner,
    MatIcon,
    MatSuffix,
  ],
  templateUrl: './company-search-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './company-search-dialog.component.css',
})
export class CompanySearchDialogComponent implements OnInit {
  readonly have_placeholder = input<boolean>(true);
  readonly have_mat_icon = input<boolean>(false);
  readonly show_only_active_company_products = input<boolean>(false);
  readonly restrict_by_permissions = input<boolean>(false);
  readonly autocomplete_panel_class = input<string>('company-location-autocomplete-panel');
  readonly option_class_prefix = input<'company-option' | 'option'>('company-option');
  readonly initial_value = input<string>('');
  readonly full_width = input<boolean>(true);

  readonly companySelected = output<CompanyChoice>();
  readonly cleared = output<void>();

  readonly company_search = new UntypedFormControl('');
  readonly company_search_term = signal('');
  readonly company_search_loading = signal(false);
  filtered_companies: CompanyChoiceCustom[] = [];
  readonly failed_company_logo_ids = signal<ReadonlySet<number>>(new Set());

  private active_company_products: CompanyProduct[] = [];
  private allowed_company_ids = new Set<number>();

  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly destroy_ref = inject(DestroyRef);

  ngOnInit(): void {
    if (this.show_only_active_company_products()) {
      this.loadActiveCompanyProducts();
    }

    this.initCompanySearch();
  }

  displayCompany(company: CompanyChoice | string | null): string {
    if (company == null || typeof company === 'string') {
      return company ?? '';
    }

    return company.label;
  }

  onCompanySelected(event: MatAutocompleteSelectedEvent): void {
    const company = event.option.value as CompanyChoice;
    this.company_search.setValue(this.displayCompany(company), { emitEvent: false });
    this.company_search_term.set('');
    this.filtered_companies = [];
    this.companySelected.emit(company);
  }

  onCompanyLogoError(company_id: number): void {
    this.failed_company_logo_ids.update(ids => new Set(ids).add(company_id));
  }

  optionClass(suffix: '' | 'logo' | 'icon' | 'text' | 'label' | 'subtitle'): string {
    const prefix = this.option_class_prefix();
    return suffix === '' ? prefix : `${prefix}-${suffix}`;
  }

  reset(): void {
    this.company_search.setValue('', { emitEvent: false });
    this.company_search_term.set('');
    this.filtered_companies = [];
    this.company_search_loading.set(false);
  }

  setDisplayValue(value: string): void {
    this.company_search.setValue(value, { emitEvent: false });
    this.company_search_term.set('');
    this.filtered_companies = [];
  }

  private initCompanySearch(): void {
    if (this.initial_value() !== '') {
      this.company_search.setValue(this.initial_value(), { emitEvent: false });
      this.company_search_term.set('');
      this.filtered_companies = [];
      this.company_search.disable({ emitEvent: false });
      return;
    }

    if (this.restrict_by_permissions()) {
      const can_create_any_company = this.permissions.isAdmin() || this.permissions.isDepartment('rrhh');
      const can_create_company = can_create_any_company || this.permissions.isProductTrioInAnyCompanyProduct();

      if (!can_create_company) {
        return;
      }

      if (!can_create_any_company) {
        this.loadAllowedCompanyIdsForProductTrio();
      }
    }

    this.company_search.valueChanges
      .pipe(
        tap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          this.company_search_term.set(term);
          if (term.length < 3) {
            this.filtered_companies = [];
            this.company_search_loading.set(false);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          if (term.length < 3) {
            return of([] as CompanyChoice[]);
          }

          this.company_search_loading.set(true);
          return this.searchCompanies(term).pipe(finalize(() => this.company_search_loading.set(false)));
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(companies => {
        this.filtered_companies = this.toCompanyChoiceCustoms(companies);
      });

    this.company_search.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(value => {
      if (value === '' || value === null) {
        this.cleared.emit();
      }
    });
  }

  private searchCompanies(term: string): Observable<CompanyChoice[]> {
    return this.api
      .getCompanies({
        search: term,
        per_page: 20,
        with: 'company_products',
        select: 'id|fact_name,company_products.id|title|logo_url|company_id',
      })
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        map(response => {
          let companies = (response.data ?? []) as CompanySearchResult[];

          if (this.show_only_active_company_products()) {
            companies = companies.filter(company => this.active_company_products.some(product => product.company_id === company.id));
          }

          let choices = companies.map(company => this.toCompanyChoice(company, term));

          if (this.restrict_by_permissions()) {
            const can_create_any_company = this.permissions.isAdmin() || this.permissions.isDepartment('rrhh');
            choices = choices.filter(company => can_create_any_company || this.allowed_company_ids.has(company.id));
          }

          return choices.sort((left, right) => left.label.localeCompare(right.label));
        }),
        catchError(() => of([] as CompanyChoice[]))
      );
  }

  private toCompanyChoice(company: CompanySearchResult, term: string): CompanyChoice {
    const normalized_term = term.toLowerCase();
    const company_products = company.company_products ?? [];
    const matched_company_product = company_products.find(company_product => company_product.title.toLowerCase().includes(normalized_term));
    const logo_company_product = company_products.find(
      company_product => company_product.logo_url != null && company_product.logo_url !== ''
    );

    return {
      id: company.id,
      label: company.fact_name,
      logo_url: logo_company_product?.logo_url ?? matched_company_product?.logo_url ?? null,
      subtitle: matched_company_product?.title ?? null,
    };
  }

  private toCompanyChoiceCustoms(companies: CompanyChoice[]): CompanyChoiceCustom[] {
    return companies.map(company => this.toCompanyChoiceCustom(company));
  }

  private toCompanyChoiceCustom(company: CompanyChoice): CompanyChoiceCustom {
    return {
      ...company,
      has_company_logo: company.logo_url != null && company.logo_url !== '' && !this.failed_company_logo_ids().has(company.id),
    };
  }

  private loadActiveCompanyProducts(): void {
    this.api
      .getCompanyProducts({
        scopes: 'isActive',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (data: ArrayResponse<CompanyProduct>) => {
          this.active_company_products = data.data ?? [];
        },
      });
  }

  private loadAllowedCompanyIdsForProductTrio(): void {
    const trio_company_product_ids = new Set(this.permissions.getProductTrioCompanyProductIds());

    if (trio_company_product_ids.size === 0) {
      this.allowed_company_ids = new Set();
      return;
    }

    this.api
      .getCompanyProducts({ per_page: 500, select: 'id|company_id' })
      .pipe(
        map(response => response.data ?? []),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(company_products => {
        this.allowed_company_ids = new Set(
          company_products
            .filter(company_product => trio_company_product_ids.has(company_product.id))
            .map(company_product => company_product.company_id)
        );
      });
  }
}

interface CompanyChoiceCustom extends CompanyChoice {
  has_company_logo: boolean;
}
